import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { OriginMark } from '@/components/admin/origin-mark';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { ReplayPill } from '@/components/admin/sessions/replay-pill';
import { protocolClosingSentence, seatModels } from '@/lib/conversation-model';
import {
  costLabel,
  exactCount,
  sessionDurationOrNothing,
  sessionStatusLabel,
  sessionStatusTone,
  sessionTime,
  tokensLabel,
} from '@/lib/session-labels';
import { isProdOrigin } from '@/lib/session-origin';
import type { SessionDetail, SessionFigures } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * The head of the conversation (§12.3): who played what, and the figures of the
 * PROTOCOL.
 *
 * ## Seven figures and not the eight of the parcours
 *
 * §12.2 puts « durée, pages, tours, coût, jetons, objectifs réussis, incidents,
 * tours malformés » at the head of the session. Here `pages` has no meaning -
 * this screen is one page of the timeline - and the duration is the
 * conversation's own span, first turn to last, which is a different figure from
 * the session's. The two yellow tiles stay yellow for the reason §12.2 gives:
 * « les deux derniers virent au jaune dès qu'ils ne sont pas nuls ».
 *
 * ## The two seats are read off the CALLS
 *
 * Not off Réglages: a screen that printed the configured model would name what
 * the next session will use, under a conversation that ran on something else.
 * `seatModels` takes the id the API answered, which is the only one that was
 * true here.
 *
 * ## A session with no turn shows a dash, never a zero
 *
 * The rule `session-tiles.tsx` states for the parcours and `sessions-table.tsx`
 * for the list: « zéro euro est une mesure, l'absence de conversation n'en est
 * pas une ». This screen is reachable on such a session by pasting the address,
 * so the two measured figures answer « - » where the counted ones - turns,
 * steps, objectives - stay true zeros.
 */

export interface ConversationHeadProps {
  detail: SessionDetail;
  figures: SessionFigures;
  budgetPerStepEur: number | null;
  /** « Sessions › la session › Protocole », built by the route that owns the links. */
  breadcrumb: ReactNode;
  /** The two controls of the row: « Exporter en » and the way back (§12.6). */
  actions: ReactNode;
  onOpenPlayedProtocol: () => void;
}

export function ConversationHead({
  detail,
  figures,
  budgetPerStepEur,
  breadcrumb,
  actions,
  onOpenPlayedProtocol,
}: ConversationHeadProps) {
  const { session, cascade } = detail;
  const first = detail.turns[0];
  const last = detail.turns[detail.turns.length - 1];
  const models = seatModels(detail.turns);
  const closed = protocolClosingSentence(detail);
  const played = figures.turnCount > 0;

  return (
    <div className="flex flex-col gap-3">
      {breadcrumb}

      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-bold text-2xl text-bleu-nuit">
              {cascade.protocol?.name ?? 'Protocole retiré'}
            </h1>
            {cascade.protocol === null ? null : (
              <span className="rounded-pill bg-vert-tint px-2.5 py-0.5 font-semibold text-vert-deep text-xs">
                version jouée v{cascade.protocol.versionNumber}
              </span>
            )}
            <StatusBadge tone={sessionStatusTone(session.status)}>
              {sessionStatusLabel(session.status)}
            </StatusBadge>
            {/* The mark « prod » of a session of the vitrine (vitrine.md §8.2, #192), as on the parcours. */}
            {isProdOrigin(session) ? (
              <OriginMark testId={SESSIONS_TEST_IDS.conversationOrigin} />
            ) : null}
            {/* The same pill as the parcours' (rejeu.md §12, #193), after the status. */}
            {session.replay === undefined || session.replay === null ? null : (
              <ReplayPill
                mode={session.replay.mode}
                testId={SESSIONS_TEST_IDS.replayConversationPill}
              />
            )}
          </div>

          <p className="text-gray-600 text-sm">
            {session.experienceName ?? 'Expérience retirée'}
            {session.experienceVersionNumber === null ? '' : ` v${session.experienceVersionNumber}`}
            {first === undefined || last === undefined
              ? ' · aucun tour joué'
              : ` · conversation de ${sessionTime(first.createdAt)} à ${sessionTime(last.createdAt)}`}
            {closed === null ? null : (
              <>
                {' · '}
                {closed.lead}
                <b className="font-semibold text-gray-700">{closed.emphasis}</b>
                {closed.tail}
              </>
            )}
          </p>

          <p className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 text-xs">
            <span>
              Persona <b className="text-gray-700">{cascade.persona?.name ?? 'aucun'}</b>
              {cascade.persona === null ? '' : ` v${cascade.persona.versionNumber}`}
            </span>
            <span>
              Interviewer <b className="text-gray-700">{models.interviewer ?? 'non enregistré'}</b>
            </span>
            <span>
              Scribe <b className="text-gray-700">{models.scribe ?? 'non enregistré'}</b>
            </span>
            {budgetPerStepEur === null ? null : (
              <span>
                Budget <b className="text-gray-700">{costLabel(budgetPerStepEur)}</b> par étape
              </span>
            )}
            <button
              type="button"
              onClick={onOpenPlayedProtocol}
              className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Le protocole joué
            </button>
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-2">{actions}</div>
      </div>

      <dl
        data-testid={SESSIONS_TEST_IDS.tiles}
        aria-label="Chiffres du protocole"
        className="grid grid-cols-2 overflow-hidden rounded-md border-gray-200 border-r border-b sm:grid-cols-4 xl:grid-cols-8"
      >
        <Tile label="Tours" value={exactCount(figures.turnCount)} />
        <Tile label="Durée" value={sessionDurationOrNothing(figures.protocolDurationMs)} />
        {/* The PROTOCOL's cost and tokens, like its duration: the analyst of the
            noyau (#149) is paid by the session and plays no turn, so it lives in
            the parcours' tiles and never in these, which the step bands sum to. */}
        <Tile label="Coût" value={played ? costLabel(figures.protocolCostEur) : '-'} />
        <Tile label="Jetons" value={played ? tokensLabel(figures.protocolTokens) : '-'} />
        <Tile label="Étapes" value={`${figures.stepsEntered} / ${figures.stepsTotal}`} />
        <Tile
          label="Objectifs réussis"
          value={`${figures.objectivesSucceeded} / ${figures.objectivesTotal}`}
        />
        <Tile
          label="Incidents"
          value={exactCount(figures.incidents)}
          warn={figures.incidents > 0}
        />
        <Tile
          label="Tours malformés"
          value={exactCount(figures.malformed)}
          warn={figures.malformed > 0}
        />
      </dl>
    </div>
  );
}

function Tile({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 border-gray-200 border-t border-l bg-gray-50 px-3.5 py-2.5">
      <dt className="font-semibold text-gray-600 text-xs uppercase tracking-wide">{label}</dt>
      <dd
        className={`m-0 truncate font-semibold text-xl tabular-nums ${
          warn === true ? 'text-jaune-deep' : 'text-gray-900'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
